<template>
  <div class="m-3 flex items-center justify-between">
    <h4 class="text-sm font-medium text-gray-900 dark:text-gray-100">
      {{ $t('app.views.orders.payment.title') || 'Payment' }}
    </h4>
    <button
      type="button"
      class="relative inline-flex h-6 w-11 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
      :class="[
        markAsPaid ? 'bg-indigo-600' : 'bg-gray-200 dark:bg-gray-700',
        disableToggle ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
      ]"
      role="switch"
      :aria-checked="markAsPaid"
      :disabled="disableToggle"
      @click="handleTogglePaid"
    >
      <span class="sr-only">{{ $t('app.views.orders.payment.mark_as_paid') || 'Mark as paid' }}</span>
      <span
        :class="markAsPaid ? 'translate-x-5' : 'translate-x-0'"
        class="pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out"
      />
    </button>
  </div>

  <div
    v-if="markAsPaid"
    class="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800"
  >
    <h5 class="mb-3 text-sm font-medium text-gray-900 dark:text-gray-100">
      {{ $t('app.views.orders.payment.select_method') || 'Select Payment Method' }}
    </h5>

    <div class="grid grid-cols-2 gap-3">
      <button
        v-for="method in paymentMethods"
        :key="method.value"
        type="button"
        class="flex items-center justify-center gap-2 rounded-md border-2 px-4 py-3 transition-all"
        :class="selectedPaymentMethod === method.value
          ? 'border-indigo-500 bg-indigo-50 text-indigo-700 dark:bg-indigo-900/20 dark:text-indigo-300'
          : 'border-gray-300 bg-white text-gray-700 hover:border-indigo-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300 dark:hover:border-indigo-600'"
        @click="$emit('update:selected-payment-method', method.value)"
      >
        <component
          :is="method.icon"
          class="h-5 w-5"
        />
        <span class="font-medium">{{ method.label }}</span>
      </button>
    </div>

    <div
      v-if="selectedPaymentMethod === 'cash'"
      class="mt-4 space-y-3"
    >
      <div class="rounded-lg border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-900">
        <label class="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
          {{ $t('app.views.orders.payment.amount_received') || 'Monto Recibido' }}
        </label>
        <div class="relative">
          <span class="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400">$</span>
          <input
            ref="cashReceivedInput"
            :value="cashReceived"
            type="number"
            step="0.01"
            min="0"
            class="block w-full rounded-md border border-gray-300 bg-white py-2 pl-7 pr-3 text-sm text-gray-900 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
            :placeholder="orderTotal.toFixed(2)"
            @input="handleCashInput"
          >
        </div>
      </div>

      <div class="grid grid-cols-2 gap-3">
        <div class="rounded-lg border border-indigo-200 bg-indigo-50 p-3 dark:border-indigo-800 dark:bg-indigo-900/20">
          <p class="mb-1 text-xs font-medium text-indigo-600 dark:text-indigo-400">
            {{ $t('app.views.orders.payment.total_to_pay') || 'Total a Pagar' }}
          </p>
          <p class="text-lg font-bold text-indigo-900 dark:text-indigo-100">
            ${{ orderTotal.toFixed(2) }}
          </p>
        </div>

        <div
          class="rounded-lg border p-3"
          :class="changeAmount >= 0
            ? 'border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-900/20'
            : 'border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/20'"
        >
          <p
            class="mb-1 text-xs font-medium"
            :class="changeAmount >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'"
          >
            {{ changeAmount >= 0 ? ($t('app.views.orders.payment.change') || 'Cambio') : ($t('app.views.orders.payment.insufficient') || 'Falta') }}
          </p>
          <p
            class="text-lg font-bold"
            :class="changeAmount >= 0 ? 'text-green-900 dark:text-green-100' : 'text-red-900 dark:text-red-100'"
          >
            ${{ Math.abs(changeAmount).toFixed(2) }}
          </p>
        </div>
      </div>

      <div
        v-if="changeAmount < 0"
        class="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-2 dark:border-red-800 dark:bg-red-900/20"
      >
        <svg
          class="mt-0.5 h-5 w-5 flex-shrink-0 text-red-600 dark:text-red-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
          />
        </svg>
        <p class="text-xs text-red-700 dark:text-red-300">
          {{ $t('app.views.orders.payment.insufficient_warning') || 'El monto recibido es insuficiente para completar el pago' }}
        </p>
      </div>
    </div>

    <p class="mt-3 text-xs text-gray-500 dark:text-gray-400">
      {{ $t('app.views.orders.payment.paid_order_warning') || 'Note: Paid orders cannot be edited.' }}
    </p>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import type { Component } from 'vue';
import type { PaymentMethod } from '@/composables/usePaymentMethods';

interface PaymentMethodWithIcon {
  value: PaymentMethod;
  label: string;
  icon: Component;
}

interface Props {
  markAsPaid: boolean;
  selectedPaymentMethod: PaymentMethod;
  orderTotal: number;
  cashReceived: number;
  changeAmount: number;
  disableToggle?: boolean;
  paymentMethods: PaymentMethodWithIcon[];
}

const props = defineProps<Props>();

const emit = defineEmits<{
  'update:mark-as-paid': [value: boolean];
  'update:selected-payment-method': [value: PaymentMethod];
  'update:cash-received': [value: number];
}>();

const cashReceivedInput = ref<HTMLInputElement | null>(null);

function handleTogglePaid() {
  if (props.disableToggle) {
    return;
  }
  emit('update:mark-as-paid', !props.markAsPaid);
}

function handleCashInput(event: Event) {
  const input = event.target as HTMLInputElement;
  emit('update:cash-received', Number(input.value) || 0);
}

function focusCashInput() {
  if (!cashReceivedInput.value) {
    return;
  }

  cashReceivedInput.value.focus();
  cashReceivedInput.value.select();
}

function blurCashInput() {
  cashReceivedInput.value?.blur();
}

defineExpose({
  focusCashInput,
  blurCashInput
});
</script>
